import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import ProductCard from "../Cards/Card";
import MobileCollectionControls, { MobileFilterActions, MobileFilterClose, useMobileCollectionDrawer } from "../Common/MobileCollectionControls";
import Loading from "../../Common/Loading";
import Pagination from "../../Common/Pagination";
import "../../Styles/CategoryProducts.css";
import {
  getproductbycategoryidRequest,
  getcategorywithsubcategoryRequest,
  getCartRequest,
  insertCartRequest,
} from "../../Containers/Home/Home-slice";
import { getcategoryRequest } from "../../Containers/Product/Product-slice";
import { getSubcategoryRequest } from "../../Containers/Category/Category-slice";
import { showSuccess } from "../../utils/Toast";
import {
  cleanNumber,
  getProductImage,
  isProductOutOfStock,
} from "../../utils/productPresentation";
import { getBuyerToken } from "../../utils/authStorage";
import { addGuestCartItem } from "../../utils/guestCart";
import { CATALOG_MENU_MODE, getCustomerSubcategories, getCustomerSubcategoryLabel } from "../../utils/catalogMenuView";

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "priceLow", label: "Price Low to High" },
  { value: "priceHigh", label: "Price High to Low" },
];
const PRODUCTS_PER_PAGE = 16;

const toggleValue = (values, nextValue) =>
  values.includes(nextValue)
    ? values.filter((value) => value !== nextValue)
    : [...values, nextValue];

function Viewcards({
  title,
  getproductbycategoryidRequest,
  productbycategoryidResponse,
  productbycategoryidLoading,
  getcategoryRequest,
  categorydata,
  insertCartRequest,
  addcartdata,
  getCartRequest,
  subcategories,
  getsubcategoryRequest,
  getcategorywithsubcategoryRequest,
  hasCustomerCategoryTree,
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [availability, setAvailability] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [priceRange, setPriceRange] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const productsTopRef = useRef(null);
  const hasMountedPageRef = useRef(false);
  const mobileDrawer = useMobileCollectionDrawer();

  useEffect(() => {
    getcategoryRequest();
    getsubcategoryRequest();
  }, [getcategoryRequest, getsubcategoryRequest]);

  useEffect(() => {
    if (!hasCustomerCategoryTree) getcategorywithsubcategoryRequest();
  }, [getcategorywithsubcategoryRequest, hasCustomerCategoryTree]);

  useEffect(() => {
    if (!id) {
      return;
    }

    getproductbycategoryidRequest(id);
  }, [getproductbycategoryidRequest, id]);

  useEffect(() => {
    setSelectedBrands([]);
    setSelectedSubcategories([]);
    setAvailability("all");
    setSortBy("newest");
    setPriceRange(null);
    setCurrentPage(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  useEffect(() => {
    if (!addcartdata?.success) {
      return;
    }

    showSuccess(addcartdata.message);
    getCartRequest();
  }, [addcartdata, getCartRequest]);

  const rawProducts = useMemo(() => {
    if (Array.isArray(productbycategoryidResponse?.products)) {
      return productbycategoryidResponse.products;
    }

    if (Array.isArray(productbycategoryidResponse?.data)) {
      return productbycategoryidResponse.data;
    }

    if (Array.isArray(productbycategoryidResponse)) {
      return productbycategoryidResponse;
    }

    return [];
  }, [productbycategoryidResponse]);

  const displayProducts = useMemo(
    () =>
      rawProducts.map((product) => {
        const basePrice = cleanNumber(product?.price);
        const discount = cleanNumber(product?.discount);
        const currentPrice =
          discount > 0 ? basePrice * (1 - discount / 100) : basePrice;

        return {
          ...product,
          title: product?.name || "Product",
          imageUrl: getProductImage(product),
          basePrice,
          price: currentPrice,
          oldPrice: discount > 0 ? basePrice : null,
          discount,
          brandKey: String(product?.brand_id ?? product?.brand_name ?? ""),
          brandLabel:
            product?.brand_name || product?.attributes?.brand || "Unbranded",
          subcategoryKey: String(product?.sub_category_id ?? ""),
          subcategoryLabel: getCustomerSubcategoryLabel(product?.sub_category_id, product?.sub_category_name),
          categoryLabel: product?.category_name || "Category",
          isOutOfStock: isProductOutOfStock(product),
          createdAtValue: new Date(product?.created_at || 0).getTime(),
        };
      }),
    [rawProducts],
  );

  const categoryRecord = useMemo(
    () => categorydata?.find((item) => String(item?.id) === String(id)),
    [categorydata, id],
  );

  const selectedCategoryLabel =
    title ||
    productbycategoryidResponse?.category?.name ||
    displayProducts[0]?.categoryLabel ||
    categoryRecord?.name ||
    "View All";

  const relatedSubcategories = useMemo(
    () =>
      (subcategories || []).filter(
        (item) => String(item?.category_id) === String(id),
      ),
    [id, subcategories],
  );

  const brandOptions = useMemo(() => {
    const map = new Map();

    displayProducts.forEach((product) => {
      if (!product.brandKey || !product.brandLabel) {
        return;
      }

      if (!map.has(product.brandKey)) {
        map.set(product.brandKey, product.brandLabel);
      }
    });

    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((first, second) => first.label.localeCompare(second.label));
  }, [displayProducts]);

  const subcategoryOptions = useMemo(() => {
    const map = new Map();

    displayProducts.forEach((product) => {
      if (!product.subcategoryKey || !product.subcategoryLabel) {
        return;
      }

      if (!map.has(product.subcategoryKey)) {
        map.set(product.subcategoryKey, product.subcategoryLabel);
      }
    });

    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((first, second) => first.label.localeCompare(second.label));
  }, [displayProducts]);

  const { minPrice, maxPrice } = useMemo(() => {
    if (!displayProducts.length) {
      return { minPrice: 0, maxPrice: 0 };
    }

    const prices = displayProducts.map((product) => cleanNumber(product.price));

    return {
      minPrice: Math.floor(Math.min(...prices)),
      maxPrice: Math.ceil(Math.max(...prices)),
    };
  }, [displayProducts]);

  useEffect(() => {
    setPriceRange([minPrice, maxPrice]);
  }, [id, maxPrice, minPrice]);

  const activeMinPrice = priceRange ? priceRange[0] : minPrice;
  const activeMaxPrice = priceRange ? priceRange[1] : maxPrice;

  const filteredProducts = useMemo(() => {
    let results = displayProducts.filter((product) => {
      const price = cleanNumber(product.price);
      return price >= activeMinPrice && price <= activeMaxPrice;
    });

    if (selectedBrands.length) {
      results = results.filter((product) =>
        selectedBrands.includes(product.brandKey),
      );
    }

    if (selectedSubcategories.length) {
      results = results.filter((product) =>
        selectedSubcategories.includes(product.subcategoryKey),
      );
    }

    if (availability === "in") {
      results = results.filter((product) => !product.isOutOfStock);
    } else if (availability === "out") {
      results = results.filter((product) => product.isOutOfStock);
    }

    if (sortBy === "priceLow") {
      results = [...results].sort(
        (first, second) => cleanNumber(first.price) - cleanNumber(second.price),
      );
    } else if (sortBy === "priceHigh") {
      results = [...results].sort(
        (first, second) => cleanNumber(second.price) - cleanNumber(first.price),
      );
    } else {
      results = [...results].sort((first, second) => {
        const firstValue = first.createdAtValue || Number(first.id) || 0;
        const secondValue = second.createdAtValue || Number(second.id) || 0;
        return secondValue - firstValue;
      });
    }

    return results;
  }, [
    activeMaxPrice,
    activeMinPrice,
    availability,
    displayProducts,
    selectedBrands,
    selectedSubcategories,
    sortBy,
  ]);

  const totalPages = Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE);
  const safeCurrentPage =
    totalPages > 0 && currentPage > totalPages ? 1 : currentPage;

  useEffect(() => {
    setCurrentPage(1);
  }, [
    activeMaxPrice,
    activeMinPrice,
    availability,
    selectedBrands,
    selectedSubcategories,
    sortBy,
  ]);

  useEffect(() => {
    if (totalPages === 0) {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!hasMountedPageRef.current) {
      hasMountedPageRef.current = true;
      return;
    }

    productsTopRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [currentPage]);

  const paginatedProducts = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  }, [filteredProducts, safeCurrentPage]);

  const hasActiveFilters =
    selectedBrands.length > 0 ||
    selectedSubcategories.length > 0 ||
    availability !== "all" ||
    sortBy !== "newest" ||
    activeMinPrice !== minPrice ||
    activeMaxPrice !== maxPrice;

  const resetFilters = () => {
    setSelectedBrands([]);
    setSelectedSubcategories([]);
    setAvailability("all");
    setSortBy("newest");
    setPriceRange([minPrice, maxPrice]);
    setCurrentPage(1);
  };

  const onAddToCart = (product) => {
    const payload = {
      product_id: product?.id,
      quantity: 1,
      price: Number(product?.basePrice || product?.price || 0),
      discount: Number(product?.discount || 0),
      attributes: product?.attributes || {},
    };

    if (!payload.product_id) {
      return;
    }

    if (!getBuyerToken()) {
      addGuestCartItem(payload);
      showSuccess("Cart Added Successfully");
      return;
    }

    insertCartRequest(payload);
  };

  if (productbycategoryidLoading && !displayProducts.length) {
    return <Loading />;
  }

  return (
    <div className="cp-page cp-page--viewall" data-view={mobileDrawer.viewMode}>
      <div className="cp-shell">
        <nav className="ds-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          <span className="sep">{">"}</span>
          <span>Categories</span>
          <span className="sep">{">"}</span>
          <span className="current">{selectedCategoryLabel}</span>
        </nav>

        {relatedSubcategories.length > 0 ? (
          <div className="cp-quick-links" aria-label="Sub-category shortcuts">
            <button
              type="button"
              className={
                selectedSubcategories.length === 0
                  ? "cp-quick-link is-active"
                  : "cp-quick-link"
              }
              onClick={() => setSelectedSubcategories([])}
            >
              All Products
            </button>

            {relatedSubcategories.map((subcategory) => (
              <button
                key={subcategory.id}
                type="button"
                className={
                  selectedSubcategories.includes(String(subcategory.id))
                    ? "cp-quick-link is-active"
                    : "cp-quick-link"
                }
                onClick={() => {
                  if (subcategory.catalog_legacy_alias) {
                    const search = new URLSearchParams({
                      type: "subcategory",
                      categoryId: String(id),
                      categoryName: selectedCategoryLabel,
                      subcategoryId: String(subcategory.id),
                      subcategoryName: subcategory.name,
                    });
                    navigate(`/categoryproducts?${search.toString()}`);
                  } else {
                    setSelectedSubcategories([String(subcategory.id)]);
                  }
                }}
              >
                {subcategory.name}
              </button>
            ))}
          </div>
        ) : null}

        {/* <section className="cp-summary-card">
          <div>
            <p className="cp-kicker">View All Products</p>
            <h1 className="cp-title">{selectedCategoryLabel}</h1>
            <p className="cp-subtitle">
              Showing {filteredProducts.length} of {displayProducts.length}{" "}
              products in this category.
            </p>
          </div>

          <div className="cp-summary-meta">
            <span className="cp-count-pill">
              {filteredProducts.length} items
            </span>
          </div>
        </section> */}

        <MobileCollectionControls title={selectedCategoryLabel} count={filteredProducts.length} sortBy={sortBy} onSort={setSortBy} drawer={mobileDrawer} />
        {mobileDrawer.filtersOpen && <button type="button" className="cp-mobile-backdrop" aria-label="Close filters" onClick={() => mobileDrawer.setFiltersOpen(false)} />}
        <div className="cp-layout">
          <aside ref={mobileDrawer.drawerRef} className={`cp-sidebar ${mobileDrawer.filtersOpen ? "is-open" : ""}`} role={mobileDrawer.filtersOpen ? "dialog" : undefined} aria-modal={mobileDrawer.filtersOpen ? "true" : undefined} aria-label="Product filters">
            <div className="cp-filter-card">
              <div className="cp-filter-header">
                <MobileFilterClose onClose={() => mobileDrawer.setFiltersOpen(false)} />
                <div>
                  <p className="cp-filter-kicker">Filters</p>
                  <h2 className="cp-filter-title">Refine results</h2>
                </div>

                <button
                  type="button"
                  className="cp-reset-button"
                  onClick={resetFilters}
                >
                  Reset
                </button>
              </div>

              <div className="cp-filter-section">
                <h3>Category</h3>
                <div className="cp-static-filter">{selectedCategoryLabel}</div>
              </div>

              <div className="cp-filter-section">
                <h3>Sub-category</h3>
                {subcategoryOptions.length > 0 ? (
                  <div className="cp-option-list">
                    {subcategoryOptions.map((option) => (
                      <label className="cp-option" key={option.value}>
                        <input
                          type="checkbox"
                          checked={selectedSubcategories.includes(option.value)}
                          onChange={() =>
                            setSelectedSubcategories((currentValues) =>
                              toggleValue(currentValues, option.value),
                            )
                          }
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="cp-empty-filter">No sub-categories available</p>
                )}
              </div>

              <div className="cp-filter-section">
                <h3>Brand</h3>
                {brandOptions.length > 0 ? (
                  <div className="cp-option-list">
                    {brandOptions.map((option) => (
                      <label className="cp-option" key={option.value}>
                        <input
                          type="checkbox"
                          checked={selectedBrands.includes(option.value)}
                          onChange={() =>
                            setSelectedBrands((currentValues) =>
                              toggleValue(currentValues, option.value),
                            )
                          }
                        />
                        <span>{option.label}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="cp-empty-filter">No brands available</p>
                )}
              </div>

              <div className="cp-filter-section">
                <h3>Price range</h3>
                <div className="cp-price-grid">
                  <label className="cp-price-field">
                    <span>Min Price</span>
                    <input
                      type="number"
                      min={0}
                      value={activeMinPrice}
                      onChange={(event) => {
                        const nextValue = Math.max(
                          0,
                          Number(event.target.value) || 0,
                        );

                        setPriceRange([
                          Math.min(nextValue, activeMaxPrice),
                          activeMaxPrice,
                        ]);
                      }}
                    />
                  </label>

                  <label className="cp-price-field">
                    <span>Max Price</span>
                    <input
                      type="number"
                      min={0}
                      value={activeMaxPrice}
                      onChange={(event) => {
                        const nextValue = Math.max(
                          0,
                          Number(event.target.value) || 0,
                        );

                        setPriceRange([
                          Math.min(activeMinPrice, nextValue),
                          Math.max(activeMinPrice, nextValue),
                        ]);
                      }}
                    />
                  </label>
                </div>

                <p className="cp-filter-note">
                  Range: AED {minPrice} to AED {maxPrice}
                </p>
              </div>

              <div className="cp-filter-section">
                <h3>Availability</h3>
                <div className="cp-option-list">
                  <label className="cp-option">
                    <input
                      type="radio"
                      name="availability"
                      checked={availability === "all"}
                      onChange={() => setAvailability("all")}
                    />
                    <span>All Products</span>
                  </label>
                  <label className="cp-option">
                    <input
                      type="radio"
                      name="availability"
                      checked={availability === "in"}
                      onChange={() => setAvailability("in")}
                    />
                    <span>In Stock</span>
                  </label>
                  <label className="cp-option">
                    <input
                      type="radio"
                      name="availability"
                      checked={availability === "out"}
                      onChange={() => setAvailability("out")}
                    />
                    <span>Out of Stock</span>
                  </label>
                </div>
              </div>

              <div className="cp-filter-section">
                <h3>Sort by</h3>
                <div className="cp-option-list">
                  {SORT_OPTIONS.map((option) => (
                    <label className="cp-option" key={option.value}>
                      <input
                        type="radio"
                        name="sortBy"
                        checked={sortBy === option.value}
                        onChange={() => setSortBy(option.value)}
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <MobileFilterActions onClose={() => mobileDrawer.setFiltersOpen(false)} />
            </div>
          </aside>

          <main className="cp-main">
            <div className="cp-results-card" ref={productsTopRef}>
              {filteredProducts.length > 0 ? (
                <>
                <div className="cp-products-grid cp-products-grid--viewall">
                  {paginatedProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      id={product.id}
                      title={product.title}
                      imageUrl={product.imageUrl}
                      price={product.price}
                      oldPrice={product.oldPrice}
                      discount={product.discount}
                      tagText={null}
                      description={product.description}
                      onAdd={() => onAddToCart(product)}
                      category_id={product.category_id}
                      company_id={product.company_id}
                      featured={product.featured}
                      status={product.status}
                      stock_quantity={product.stock_quantity}
                      item={product}
                    />
                  ))}
                </div>
                <Pagination
                  currentPage={safeCurrentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
                </>
              ) : (
                <div className="cp-empty-state">
                  <h3>No products found</h3>
                  <p>
                    Try adjusting your filters or reset them to explore more
                    available products in this category.
                  </p>
                  {hasActiveFilters ? (
                    <button
                      type="button"
                      className="cp-empty-action"
                      onClick={resetFilters}
                    >
                      Reset Filters
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  productbycategoryidResponse: state.Home.getproductbycategoryiddata,
  productbycategoryidLoading: state.Home.getproductbycategoryidLoading,
  categorydata: state.Product.categorydata?.data || [],
  addcartdata: state.Home.Cartdata,
  subcategories:
    getCustomerSubcategories(state.Home.getcategorywithsubcategorydata?.data) ||
    (CATALOG_MENU_MODE === "legacy" ? [] : state.Category.getSubcategorydata?.data || []),
  hasCustomerCategoryTree: Array.isArray(state.Home.getcategorywithsubcategorydata?.data),
});

const mapDispatchToProps = (dispatch) => ({
  getproductbycategoryidRequest: (categoryId) =>
    dispatch(getproductbycategoryidRequest(categoryId)),
  getcategoryRequest: () => dispatch(getcategoryRequest()),
  insertCartRequest: (payload) => dispatch(insertCartRequest(payload)),
  getCartRequest: () => dispatch(getCartRequest()),
  getsubcategoryRequest: () => dispatch(getSubcategoryRequest()),
  getcategorywithsubcategoryRequest: () => dispatch(getcategorywithsubcategoryRequest()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Viewcards);
